import type { AxisSelection } from '@atlas/types'
import { cleanDrafts } from '@/components/work-orders/OriginalCodes'
import type { UpdateWorkOrderInput, WorkOrder } from '@/domain/work-order.types'
import {
	BRAND_AXIS,
	briefingHtml,
	cleanLinks,
	PRODUCT_FIELD,
	parsedDuration,
	toIsoDateTime,
	tupleFromForm,
	type WorkOrderFormState,
} from './form-model'
import { moneyOf } from './form-model.external'

// The Edit form's save: the PATCH body holds only what the user changed since the form loaded.

/** Every PATCH field except the tuple, in its wire shape: a cleared optional field is `null`. */
function patchFieldsOf(form: WorkOrderFormState): Omit<UpdateWorkOrderInput, 'tuple'> {
	return {
		title: form.title.trim(),
		briefing: briefingHtml(form.briefing) || null,
		dueAt: toIsoDateTime(form.dueAt) ?? null,
		parentWorkOrderId: form.parentWorkOrderId ?? null,
		pmUserId: form.pmUserId.trim() || null,
		isContentFactory: form.isContentFactory,
		// The two mandatory descriptors cannot be cleared (the schema refuses `null`), so a blank
		// one is left out rather than sent as a clear.
		complexityLevel: form.complexityLevel || undefined,
		assetTypeCode: form.assetTypeCode || undefined,
		veevaCode: form.veevaCode.trim() || null,
		expectedPublishingDate: form.expectedPublishingDate || null,
		budget: moneyOf(form.budget),
		plannedCost: moneyOf(form.plannedCost),
		finalCost: moneyOf(form.finalCost),
		originalCodes: cleanDrafts(form.originalCodes),
		campaignCode: form.campaignCode.trim() || null,
		durationSeconds: parsedDuration(form.durationSeconds) ?? null,
		links: cleanLinks(form.links),
		assigneeIds: form.assigneeId.trim() ? [form.assigneeId.trim()] : [],
	}
}

/**
 * The tuple to send, or undefined when no context field changed.
 *
 * The write replaces the whole tuple, so it starts from the order's STORED tuple and swaps in
 * only the axes the user changed. The form holds one value per axis, and an order can hold
 * several (a Workfront order's audiences, its brand beside its product): rebuilding the tuple
 * from the form would drop the rest on an unrelated edit.
 */
function changedTuple(
	form: WorkOrderFormState,
	initial: WorkOrderFormState,
	stored: AxisSelection,
): AxisSelection | undefined {
	const changed = Object.keys({ ...form.tuple, ...initial.tuple }).filter(
		(key) => (form.tuple[key] || undefined) !== (initial.tuple[key] || undefined),
	)
	if (changed.length === 0) return undefined
	const next = tupleFromForm(form)
	const tuple: AxisSelection = { ...stored }
	for (const key of changed) {
		// The Product field is a view of the brand axis: either changing rewrites that axis.
		const axis = key === PRODUCT_FIELD ? BRAND_AXIS : key
		if (next[axis]) tuple[axis] = next[axis]
		else delete tuple[axis]
	}
	return tuple
}

/**
 * Assemble a **minimal** PATCH body: only the fields the user changed since the form loaded
 * (`initial`, the {@link fromWorkOrder} of `original`). A field left untouched is omitted, so
 * a value the form cannot show in full, or rounds on the way in (a due time's seconds), is
 * never written back, and an unrelated edit does not re-classify the order. Cleared optional
 * fields become `null` (the schema is nullable → truly unset).
 */
export function toUpdateInput(
	form: WorkOrderFormState,
	initial: WorkOrderFormState,
	original: WorkOrder,
): UpdateWorkOrderInput {
	const next = patchFieldsOf(form)
	const before = patchFieldsOf(initial)
	const patch: UpdateWorkOrderInput = {}
	for (const key of Object.keys(next) as (keyof typeof next)[]) {
		if (JSON.stringify(next[key]) !== JSON.stringify(before[key])) Object.assign(patch, { [key]: next[key] })
	}
	const tuple = changedTuple(form, initial, original.tuple)
	if (tuple) patch.tuple = tuple
	return patch
}
